import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import WelcomeModal from "./WelcomeModal";
import GoalCards from "./GoalCards";
import SpotlightTour from "./SpotlightTour";

/**
 * TourProvider — mounted once inside DashboardLayout.
 * Owns the first-login welcome modal, the "What would you like to do first?"
 * goal cards, and the spotlight product tour. Listens for global events so
 * any page (Help → Getting Started, future AI Assistant) can launch flows:
 *   window.dispatchEvent(new CustomEvent("bfa:start-tour"))
 *   window.dispatchEvent(new CustomEvent("bfa:open-goals"))
 */
export default function TourProvider() {
  const [boot, setBoot] = useState(null);
  const [view, setView] = useState("");   // "" | welcome | goals | tour

  useEffect(() => {
    api.get("/tour/bootstrap").then((r) => {
      setBoot(r.data);
      if (r.data.show_welcome && sessionStorage.getItem("bfa_welcome_snoozed") !== "1") {
        setView("welcome");
      }
    }).catch(() => {});
  }, []);

  const startTour = useCallback(async () => {
    try {
      const r = await api.get("/tour/bootstrap");
      setBoot(r.data);
      await api.post("/tour/restart");
      await api.post("/tour/welcome", { action: "tour" });
      setView("tour");
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    const onStart = () => startTour();
    const onGoals = () => setView("goals");
    window.addEventListener("bfa:start-tour", onStart);
    window.addEventListener("bfa:open-goals", onGoals);
    return () => {
      window.removeEventListener("bfa:start-tour", onStart);
      window.removeEventListener("bfa:open-goals", onGoals);
    };
  }, [startTour]);

  // Mark "explored a studio" once — powers the Getting Started checklist
  useEffect(() => {
    if (!boot || boot.state?.studio_explored) return;
    const STUDIO_ROUTES = ["/dashboard/podcast", "/dashboard/vision-studio", "/dashboard/magazine",
      "/dashboard/music-studio", "/dashboard/audio-studio", "/dashboard/avatar-studio",
      "/dashboard/imaging", "/dashboard/production-suite", "/dashboard/generate"];
    const check = () => {
      if (STUDIO_ROUTES.some((r) => window.location.pathname.startsWith(r))) {
        api.post("/tour/studio-explored").catch(() => {});
        setBoot((b) => (b ? { ...b, state: { ...b.state, studio_explored: true } } : b));
        return true;
      }
      return false;
    };
    if (check()) return;
    const id = setInterval(() => { if (check()) clearInterval(id); }, 4000);
    return () => clearInterval(id);
  }, [boot]);

  if (!boot || !boot.enabled) return null;

  const welcomeAction = async (action, dontShowAgain) => {
    try { await api.post("/tour/welcome", { action, dont_show_again: dontShowAgain }); } catch { /* noop */ }
    if (action === "skip" && !dontShowAgain) sessionStorage.setItem("bfa_welcome_snoozed", "1");
    if (action === "tour") setView("tour");
    else if (action === "goals") setView("goals");
    else setView("");
  };

  return (
    <>
      {view === "welcome" && (
        <WelcomeModal firstName={boot.first_name} onAction={welcomeAction} />
      )}
      {view === "goals" && (
        <GoalCards onClose={() => setView("")} onFullTour={() => setView("tour")} />
      )}
      {view === "tour" && (
        <SpotlightTour
          steps={boot.steps || []}
          startIndex={boot.state?.tour_completed ? 0 : boot.state?.current_tour_step || 0}
          onExit={() => setView("")}
        />
      )}
    </>
  );
}
